import { Link } from '@tanstack/react-router'
import {
  BookOpen,
  LogOut,
  Monitor,
  Moon,
  Settings,
  Sun,
  User,
} from 'lucide-react'
import { useTheme } from '@/app/providers/ThemeProvider'
import agentImage from '@/assets/images/agent.png'
import logoImage from '@/assets/images/logo.png'
import { AIAssistantDrawer } from '@/features/ai-assistant/components/AIAssistantDrawer'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { DocumentationDrawer } from '@/features/documentation/components/DocumentationDrawer'
import { ConnectedEnvironmentSelector } from '@/features/environments/components/ConnectedEnvironmentSelector'
import { TenantSwitcher } from '@/shared/components/tenant'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { cn } from '@/shared/lib/utils'

interface TopNavProps {
  className?: string
  onSettingsClick?: () => void
  breadcrumbs?: React.ReactNode
}

export function TopNav({
  className,
  onSettingsClick,
  breadcrumbs,
}: TopNavProps) {
  const { user, logout } = useAuthStore()
  const { setTheme } = useTheme()

  const handleLogout = async () => {
    await logout()
    window.location.href = '/login'
  }

  // Get user initials for avatar fallback
  const getUserInitials = (email: string | null | undefined) => {
    if (!email) return 'U'
    return email.charAt(0).toUpperCase()
  }

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60',
        className
      )}
    >
      <div className='flex h-full items-center justify-between px-6'>
        {/* Left side - Logo, Organization, and Environment */}
        <div className='flex items-center gap-4'>
          <div className='flex items-center gap-3'>
            <img src={logoImage} alt='Logo' className='w-10 h-10' />
            <span className='text-muted-foreground'>/</span>
            <TenantSwitcher className='max-w-[200px]' />
            <span className='text-muted-foreground'>/</span>
            <ConnectedEnvironmentSelector onSettingsClick={onSettingsClick} />
            {breadcrumbs && (
              <>
                <span className='text-muted-foreground'>/</span>
                <div className='text-sm'>{breadcrumbs}</div>
              </>
            )}
          </div>
        </div>

        {/* Right side - Actions and User */}
        <div className='flex items-center gap-3'>
          {/* Documentation Button */}
          <DocumentationDrawer
            trigger={
              <Button variant='ghost' size='sm' className='gap-2'>
                <BookOpen className='h-4 w-4' />
                <span className='hidden sm:inline'>Documentation</span>
              </Button>
            }
          />

          {/* AI Assistant Button */}
          <AIAssistantDrawer
            trigger={
              <Button variant='ghost' size='sm' className='gap-2'>
                <img src={agentImage} alt='Agent' className='w-8 h-8' />
                <span className='hidden sm:inline'>Cooboy</span>
              </Button>
            }
          />

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='rounded-full p-1'>
                <Avatar className='h-8 w-8'>
                  <AvatarImage
                    src={user?.avatar_url ?? ''}
                    alt={user?.name || user?.email || 'User'}
                  />
                  <AvatarFallback>
                    {getUserInitials(user?.email)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className='w-56' align='end' sideOffset={4}>
              <DropdownMenuLabel className='font-normal'>
                <div className='flex flex-col space-y-1'>
                  <p className='text-sm font-medium leading-none'>
                    {user?.email || 'User'}
                  </p>
                  <p className='text-xs leading-none text-muted-foreground'>
                    Admin
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <Link to='/settings/profile'>
                <DropdownMenuItem>
                  <User className='mr-2 h-4 w-4' />
                  Profile Settings
                </DropdownMenuItem>
              </Link>
              <Link to='/settings/account'>
                <DropdownMenuItem>
                  <Settings className='mr-2 h-4 w-4' />
                  Account Settings
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setTheme('light')}>
                <Sun className='mr-2 h-4 w-4' />
                Light Theme
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('dark')}>
                <Moon className='mr-2 h-4 w-4' />
                Dark Theme
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme('system')}>
                <Monitor className='mr-2 h-4 w-4' />
                System Theme
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className='text-red-600'>
                <LogOut className='mr-2 h-4 w-4' />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}
